import type { UserTypeEnum } from '@snowit/models';
import type { MainNavRoute, MainPath, NavRoute } from '../types';

import PERMISSIONS from '@snowit/utilities/permissions';

import { makeChildren } from '../utils';

export const USERS_TABS = [
  'role',
  'info',
  'profiles',
  'orders',
  'memberships',
  'snowitcards',
  'purchased',
  'notes',
  'cart',
  'referral',
  'credentials',
  'emails',
  'wallet',
  'export_presets',
  'epp',
] as const;

export type UsersTabs = (typeof USERS_TABS)[number];

// const makeChildren = () => [];

export const UsersPath: MainPath = '/users/:type?/:id?/:section?/:subId?';
export const ROLES_TABS = ['info', 'export_presets'] as const;
export type RolesTabs = (typeof ROLES_TABS)[number];

export const RolesPath: MainPath = '/roles/:id?/:section?';
export const PermissionsPath: MainPath = '/permissions';
export const BrevoEmailsPath: MainPath = '/user_emails';
export const UserCreatePath: MainPath = '/user_create';
export const ProfilesPath: MainPath = '/profiles';

const usersChildren: NavRoute<UsersTabs>[] = makeChildren<UsersTabs>({
  role: {
    path: 'role',
    permissions: [PERMISSIONS.USERS__ROLES],
  },
  info: {
    path: 'info',
    permissions: [PERMISSIONS.USERS__INFO],
  },
  profiles: {
    path: 'profiles',
    children: [{ path: ':id', disowned: true, permissions: [PERMISSIONS.USERS__PROFILES_DETAILS] }],
    permissions: [PERMISSIONS.USERS__PROFILES],
  },
  orders: {
    path: 'orders',
    permissions: [PERMISSIONS.USERS__ORDERS],
  },
  memberships: {
    path: 'memberships',
    permissions: [PERMISSIONS.USERS__MEMBERSHIPS],
  },
  snowitcards: {
    path: 'snowitcards',
    permissions: [PERMISSIONS.USERS__SNOWITCARDS],
  },
  purchased: {
    path: 'purchased',
    permissions: [PERMISSIONS.USERS__ITEMS],
  },
  notes: {
    path: 'notes',
    permissions: [PERMISSIONS.USERS__NOTE_VIEW],
  },
  cart: {
    path: 'cart',
    permissions: [PERMISSIONS.USERS__CART],
  },
  referral: {
    path: 'referral',
    permissions: [PERMISSIONS.USERS__REFERRALS],
  },
  credentials: {
    path: 'credentials',
    permissions: [PERMISSIONS.USERS__CREDENTIALS],
  },
  export_presets: {
    path: 'export_presets',
    permissions: [PERMISSIONS.USERS__ROLES_EDIT],
  },
  emails: {
    path: 'emails',
    type: 0,
  },
  wallet: {
    path: 'wallet',
    type: 0,
  },
  epp: {
    path: 'epp',
    permissions: [PERMISSIONS.USERS__EPP],
  },
});

const usersTypeChild: NavRoute = {
  path: ':id',
  disowned: true,
  permissions: [PERMISSIONS.USERS__DETAILS],
  children: usersChildren,
};

export const UsersRoute: MainNavRoute = {
  path: '/users',
  label: 'routes.users',
  icon: 'user',
  type: 1,
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-24341/4477a3e35baa09c',
  permissions: [PERMISSIONS.USERS__LIST],
  children: makeChildren<UserTypeEnum | ':id'>({
    USER: {
      path: 'USER',
      children: [usersTypeChild],
    },
    PARTNER: {
      path: 'PARTNER',
      children: [usersTypeChild],
      permissions: [PERMISSIONS.USERS__VIEW_PARTNER],
    },
    ADMIN: {
      path: 'ADMIN',
      children: [usersTypeChild],
      permissions: [PERMISSIONS.USERS__VIEW_ADMIN],
    },
    ':id': {
      path: ':id',
      disowned: true,
      permissions: [PERMISSIONS.USERS__DETAILS],
      children: usersChildren,
    },
  }),
};

const rolesChildren: NavRoute<RolesTabs>[] = makeChildren<RolesTabs>({
  info: {
    path: 'info',
    permissions: [PERMISSIONS.ROLES__EDIT],
  },
  export_presets: {
    path: 'export_presets',
    permissions: [PERMISSIONS.ROLES__EDIT],
  },
});

export const RolesRoute: MainNavRoute = {
  path: '/roles',
  label: 'routes.users_roles',
  icon: 'crown',
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23661/317d0ac0fb3b65d',
  permissions: [PERMISSIONS.ROLES__LIST],
  type: 0,
  children: [
    {
      path: ':id',
      permissions: [PERMISSIONS.ROLES__EDIT],
      children: rolesChildren,
    },
  ],
};

export const PermissionsRoute: MainNavRoute = {
  path: '/permissions',
  label: 'routes.users_permissions',
  icon: 'card_badge',
  kb: 'https://doc.clickup.com/20614177/p/h/kn311-23681/c2c3d7f899842ba',
  type: 0,
  permissions: [PERMISSIONS.PERMISSIONS__LIST],
};

export const BrevoEmailRoute: MainNavRoute = {
  path: '/user_emails',
  label: 'routes.users_emails',
  icon: 'email',
  type: 0,
};

export const UserCreateRoute: MainNavRoute = {
  path: '/user_create',
  label: 'routes.user_create',
  icon: 'plus',
  type: 1,
  permissions: [PERMISSIONS.USERS__CREATE],
};

export const ProfilesRoute: MainNavRoute = {
  path: '/profiles',
  label: 'routes.profiles',
  icon: 'user',
  type: 1,
  permissions: [PERMISSIONS.USERS__PROFILES],
};
